import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { supabase, logActivity } from '../lib/supabase';
import { writeQuoteUrlToDeal, updateDealDescription, updateDeal, captureHubspotSnapshot, pushHubspotGapFill, pushHubspotOverwrite } from '../lib/hubspot';
import { useAuth } from '../contexts/AuthContext';
import AddendumsModal from '../components/AddendumsModal';
import ServiceOrderPreviewModal from '../components/ServiceOrderPreviewModal';
import EngagementPanel from '../components/EngagementPanel';
import { loadAddendumTemplates, validateAddendums } from '../lib/addendums';
// v3.5.67: per-user quote-type permissions
import { userCanCreateQuoteType } from '../lib/quotePermissions';
import { useConfig } from '../contexts/ConfigContext';
import { lookupZip, lookupZipFromUSPS, fmtPct, gmColor, gmBg, getBurdenedRate } from '../lib/pricing';
import { buildRateSheet, fmtRate } from '../lib/rateSheet';
// v3.5.92.2: unified totals utility — FlexIT migration (Phase 1 item 2, 3/4).
// See CLAUDE.md §10b. FlexIT is the simplest of the five — no recurring MRR,
// no onboarding incentive (people buying time know they're buying time),
// no ITSO. The flex block IS the upfront fee (not a separate MRR layer like
// on IT/Multi-Site/Bundle).
import { buildTotals } from '../lib/quoteTotals';
import { getOrAnalyzeMarket } from '../lib/marketRates';
import { saveQuoteVersion } from '../lib/quoteVersions';
import QuoteNotes   from '../components/QuoteNotes';
import QuoteHistory from '../components/QuoteHistory';
import HubSpotConnect from '../components/HubSpotConnect';
import SPTConnect     from '../components/SPTConnect';
import RateSheetModalComp from '../components/RateSheetModal';
import StripePaymentCard from '../components/StripePaymentCard';
import SignatureCard from '../components/SignatureCard';
import AgreementEffectiveDate from '../components/AgreementEffectiveDate';
import MarketRateCard from '../components/MarketRateCard';
import FlexTimeSelector from '../components/FlexTimeSelector';
import { calcFlexBlock } from '../lib/flexTime';
import { createFlexITSPTProposal, buildFlexITQuoteShape } from '../lib/smartPricingTable';
import { ReviewBanner } from '../components/SendForReview';
import QuoteSaveBar from '../components/QuoteSaveBar';
import RepSelector from '../components/RepSelector';
// v3.8.9 — Unified-UX workspace shell (flag ferrumiq_v3_flexit, default off).
import { WorkspaceShell, QuoteHeader, LiveTotalCard, RailCard, SubCard, Button as V3Button } from '../components/v3';
import { isQuoteLocked, QUOTE_LOCK_REASON } from '../lib/quoteLock';
import LockedQuoteBanner from '../components/LockedQuoteBanner';
import QuoteDocuments from '../components/QuoteDocuments';
import { v3 } from '../lib/styleTokens';

// ── FlexIT fixed assumptions (from PDF) ──────────────────────────────────────
const FLEXIT_ASSUMPTIONS = [
  'Client engages Ferrum Technology Services on an as-needed, break/fix basis with no ongoing managed services agreement in place.',
  'All services are rendered on a time and materials basis. Rates, billing increments, after-hours premiums, and holiday rates are defined in the Rate Card attached to this agreement.',
  'No service level commitments, guaranteed response times, or priority scheduling are implied or guaranteed under this plan.',
  'Client is responsible for providing timely access to systems, personnel, and facilities. Delays caused by lack of access may result in additional billable time.',
  'Onsite dispatch is subject to technician availability. Same-day or emergency dispatch is not guaranteed and may be subject to rates outlined in the Rate Card.',
  'Client acknowledges that time and materials engagements are not capped unless a not-to-exceed amount is explicitly agreed upon in writing prior to work commencing.',
  'Hardware, software, licensing, and third-party services procured on behalf of the client are billed separately. All such sales are final — no returns or refunds.',
];

// ── Helper components ─────────────────────────────────────────────────────────
function Sec({ t, c, children }) {
  return (
    <div style={{ marginBottom:10 }}>
      <div style={{ display:'flex', alignItems:'center', gap:4, marginBottom:5, paddingBottom:3, borderBottom:'1px solid #f1f5f9' }}>
        <div style={{ width:2, height:11, background:c||'#f97316', borderRadius:2 }}/>
        <span style={{ fontSize:9, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'#6b7280' }}>{t}</span>
      </div>
      {children}
    </div>
  );
}
function Fld({ lbl, s, children }) {
  return (
    <div style={{ marginBottom:4, ...s }}>
      <label style={{ display:'block', fontSize:9, fontWeight:600, color:'#374151', marginBottom:1 }}>{lbl}</label>
      {children}
    </div>
  );
}
function TI({ value, onChange, placeholder }) {
  return <input value={value||''} onChange={e => onChange(e.target.value)} placeholder={placeholder||''} style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, outline:'none' }}/>;
}
function NI({ v, s }) {
  return <input type="number" value={v} min={0} step={0.5} onChange={e => s(parseFloat(e.target.value)||0)} style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace', color:'#1e3a5f', background:'#eff6ff', fontWeight:600, outline:'none' }}/>;
}
function SI({ v, s, opts }) {
  return <select value={v} onChange={e => s(e.target.value)} style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, background:'white', outline:'none', color:'#374151' }}>{opts.map(([a,b]) => <option key={a} value={a}>{b}</option>)}</select>;
}
// v3.5.99.7.13.1: Grid2 helper added. The v3.5.99.7.6 patch added a
// <Grid2> reference at the Office/Mobile Phone row but didn't include
// the helper definition (other 4 quote pages have it locally). Latent
// bug that only surfaced when the FlexIT page was actually loaded —
// caused a white-screen "Grid2 is not defined" ReferenceError.
function Grid2({ children }) {
  return <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:5 }}>{children}</div>;
}
const fmt$0 = n => n != null ? `$${Math.round(n).toLocaleString('en-US')}` : '—';
const fmt$2 = n => n != null ? `$${Number(n).toFixed(2)}` : '—';

// ── Main component ────────────────────────────────────────────────────────────
export default function FlexITQuotePage() {
  const { id }        = useParams();
  const navigate      = useNavigate();
  const location      = useLocation();
  const { profile, isAdmin } = useAuth();
  // v3.5.67: quote-type permission gate (see QuotePage for full rationale).
  const _isNewQuote = !id;
  const _hasPermission = userCanCreateQuoteType(profile, 'flexit');
  const isReadOnly = !_isNewQuote && profile != null && !_hasPermission;
  useEffect(() => {
    if (_isNewQuote && profile != null && !_hasPermission) {
      navigate('/quotes', { replace: true, state: { permissionDenied: 'flexit' } });
    }
  }, [profile, _isNewQuote, _hasPermission, navigate]);
  const { settings, loading: configLoading } = useConfig();

  // Quote meta
  const [existingQuote,  setExistingQuote]  = useState(null);
  const [quoteStatus,    setQuoteStatus]    = useState('draft');
  const [proposalName,   setProposalName]   = useState('');
  const [saving,         setSaving]         = useState(false);
  const [saveMsg,        setSaveMsg]        = useState('');

  // Rep
  const [repId,        setRepId]        = useState(null);
  const [repProfile,   setRepProfile]   = useState(null);
  const [teamMembers,  setTeamMembers]  = useState([]);

  // Client
  const [recipientBiz,     setRecipientBiz]     = useState('');
  const [recipientContact, setRecipientContact] = useState('');
  const [recipientEmail,   setRecipientEmail]   = useState('');
  const [recipientAddress, setRecipientAddress] = useState('');
  // v3.5.99.7.3: client phone/mobile pulled from HubSpot in connectDeal.
  const [recipientPhone, setRecipientPhone] = useState('');
  const [recipientMobilePhone, setRecipientMobilePhone] = useState('');
  // v3.5.99.7.4: HubSpot write-back state. contactId/companyId route
  // updateContact/updateCompany calls. snapshot captures HubSpot's original
  // values at connectDeal time so gap-fill knows which fields to push.
  const [hubContactId, setHubContactId] = useState('');
  const [hubCompanyId, setHubCompanyId] = useState('');
  const [hubspotSnapshot, setHubspotSnapshot] = useState(null);
  // v3.5.99.7.6: manual full-overwrite push state. Distinct from the
  // existing hubMsg/hubSyncing state (which is QuotePage-only and used
  // by the older syncToDeal button). hubPushing/hubPushMsg track the
  // new pushAllToHubspot flow.
  const [hubPushing, setHubPushing] = useState(false);
  const [hubPushMsg, setHubPushMsg] = useState('');

  // v3.5.99.7.6: manual full-overwrite push to HubSpot. Unlike the
  // automatic gap-fill (which only pushes fields HubSpot was originally
  // missing), this pushes ALL current Ferrum values, overwriting whatever
  // is in HubSpot. Use when rep wants to force HubSpot to match Ferrum's
  // current state — typically after correcting a phone number or address
  // in Ferrum that HubSpot has stale.
  async function pushAllToHubspot() {
    if (!hubContactId && !hubCompanyId) {
      setHubPushMsg('No HubSpot deal linked — connect a deal first.');
      return;
    }
    setHubPushing(true); setHubPushMsg('');
    try {
      const r = await pushHubspotOverwrite({
        contactId: hubContactId, companyId: hubCompanyId,
        recipientContact, recipientEmail, recipientPhone, recipientMobilePhone, recipientAddress,
      });
      if (r.updatedSnapshot) setHubspotSnapshot(r.updatedSnapshot);
      const total = r.contactFields.length + r.companyFields.length;
      if (total > 0) {
        setHubPushMsg(`✓ Pushed ${total} field${total > 1 ? 's' : ''} (${r.contactFields.length} contact, ${r.companyFields.length} company)`);
      } else {
        setHubPushMsg('Nothing to push — all recipient fields are empty.');
      }
      if (r.errors?.length) {
        console.warn('HubSpot push errors:', r.errors);
        setHubPushMsg(prev => prev + ' · ⚠ some errors (see console)');
      }
    } catch (e) {
      setHubPushMsg('✗ ' + (e.message || 'Push failed'));
    }
    setHubPushing(false);
  }
  // v3.5.99.0: per-quote override for proposal-approved notification CC list.
  const [notificationCc,   setNotificationCc]   = useState('');
  // v3.5.99.4: per-quote addendums — see QuotePage canonical block for rationale.
  const [addendums,         setAddendums]         = useState([]);
  // v3.5.99.7.24: optional back-dated agreement effective date (YYYY-MM-DD)
  const [effectiveDate,     setEffectiveDate]     = useState('');
  const [addendumTemplates, setAddendumTemplates] = useState([]);
  const [addendumsModalOpen, setAddendumsModalOpen] = useState(false);
  // v3.5.99.5: Service Order preview modal — see QuotePage canonical block
  const [soPreviewModalOpen, setSoPreviewModalOpen] = useState(false);
  useEffect(() => { loadAddendumTemplates().then(setAddendumTemplates); }, []);
  const [clientZip,        setClientZip]        = useState('');
  const [zipResult,        setZipResult]        = useState(null);
  const [marketCity,       setMarketCity]       = useState('');
  const [marketState,      setMarketState]      = useState('');

  // Market analysis
  const [marketAnalysis, setMarketAnalysis] = useState(null);
  const [marketLoading,  setMarketLoading]  = useState(false);
  const [marketError,    setMarketError]    = useState('');
  // Locked-in market rates from MarketRateCard accept (or saved rate sheet load).
  // Held independently of marketAnalysis so it survives the race condition where
  // handleZipChange completes AFTER MarketRateCard fires onRatesAccepted — without
  // this, accepting a rate override and then having handleZipChange finish second
  // would silently overwrite the override with the original analysis.
  const [acceptedRates,  setAcceptedRates]  = useState(null);

  // FlexIT-specific
  const [prepayHours,    setPrepayHours]    = useState(2);
  const [overridePrepay, setOverridePrepay] = useState(false);
  const [prepayOverride, setPrepayOverride] = useState('');
  const [notes,          setNotes]          = useState('');
  const [flexHours,      setFlexHours]      = useState(null);
  const [flexitFocusedSection, setFlexitFocusedSection] = useState(null);  // v3.8.9 workspace

  // Integrations
  const [hubDealId,      setHubDealId]      = useState('');
  const [hubDealUrl,     setHubDealUrl]     = useState('');
  const [hubDealName,    setHubDealName]    = useState('');
  const [hubDescription, setHubDescription]= useState('');
  const [sptProposalId,  setSptProposalId]  = useState(null);

  // Computed rates — accepted rates always win, then live analysis, then admin default.
  // This makes the flex block, the prepay calc, the FlexTimeSelector, and the rate
  // sheet preview all use the same locked-in labor rate.
  const effectiveRates = acceptedRates
    ? { ...(marketAnalysis?.rates || {}), ...acceptedRates }
    : (marketAnalysis?.rates || null);
  const rateSheetAnalysis = marketAnalysis
    ? { ...marketAnalysis, rates: effectiveRates || marketAnalysis.rates }
    : null;
  const rateSheet    = rateSheetAnalysis ? buildRateSheet({ analysis: rateSheetAnalysis, settings, clientName: recipientBiz, recipientContact }) : null;
  const remoteRate   = effectiveRates?.remote_support ?? parseFloat(settings?.oos_remote_rate || 165);
  // When a flex block is selected the flex block IS the upfront fee — no separate prepay.
  const flexBlock    = (flexHours && remoteRate) ? calcFlexBlock(flexHours, remoteRate, settings) : null;
  const hasFlexBlock = !!flexBlock;
  const prepayAmount = hasFlexBlock
    ? 0
    : (overridePrepay && prepayOverride
        ? parseFloat(prepayOverride) || 0
        : Math.round(prepayHours * remoteRate * 100) / 100);

  // ── v3.5.56: Implied gross margin ────────────────────────────────────────────
  // Revenue = block price (when a flex block is selected) OR dispatch deposit
  // (when no block — just the 2-hour-default initial prepayment).
  // Cost = labor hours × burdened_hourly_rate from pricing_settings (default
  // $80/hr as of v3.5.58 — was $125 fallback; all five quote types compute
  // GM consistently against the same loaded labor cost via getBurdenedRate).
  // GM = (revenue − cost) / revenue.
  //
  // Block discounts compress GM (revenue is reduced but cost is not), which
  // is the same behavior managed IT flex blocks show in QuotePage line ~929.
  // Reps see the resulting margin in the KPI strip and on the saved-quotes
  // list, giving them visibility before sending the quote.
  //
  // When neither a block nor any prepay is set (revenue = 0), GM is null and
  // we display "—" rather than 0% (which would imply a real-but-zero margin).
  const burdenedHourlyRate = getBurdenedRate(settings);
  const flexitRevenue      = hasFlexBlock ? (flexBlock?.blockPrice || 0) : prepayAmount;
  const flexitLaborHours   = hasFlexBlock ? (flexBlock?.hours || 0)      : prepayHours;
  const flexitLaborCost    = Math.round(flexitLaborHours * burdenedHourlyRate * 100) / 100;
  const flexitImpliedGM    = flexitRevenue > 0
    ? (flexitRevenue - flexitLaborCost) / flexitRevenue
    : null;

  // ── Rep effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    supabase.from('profiles').select('id, full_name, email, commission_rate').order('full_name')
      .then(({ data }) => setTeamMembers(data || []));
  }, []);
  useEffect(() => {
    if (!repId && profile?.id && !id) setRepId(profile.id);
  }, [profile, id]);
  useEffect(() => {
    if (!repId || !teamMembers.length) return;
    const rep = teamMembers.find(m => m.id === repId);
    if (rep) setRepProfile(rep);
  }, [repId, teamMembers]);

  // ── Load market analysis for ZIP ────────────────────────────────────────────
  async function handleZipChange(zip, opts = {}) {
    const { readOnly = false } = opts;
    setClientZip(zip);
    if (zip?.length !== 5) return;
    const zr = lookupZip(zip);
    setZipResult(zr);
    setMarketLoading(true);
    setMarketError('');
    try {
      // getOrAnalyzeMarket fetches existing analysis OR runs AI to create one when none exists.
      // Pass city/state hints so AI can resolve unfamiliar ZIPs faster.
      const cityHint  = zr?.name?.split(',')[0] || undefined;
      const stateHint = zr?.state || undefined;
      const res = await getOrAnalyzeMarket(zip, false, cityHint, stateHint, readOnly);
      if (res?.analysis) {
        setMarketAnalysis(res.analysis);
        setMarketCity(res.analysis.city || cityHint || '');
        setMarketState(res.analysis.state || stateHint || '');
      } else if (zr) {
        // No analysis available (readOnly mode + no existing record). Fall back to ZIP table.
        setMarketCity(cityHint || '');
        setMarketState(stateHint || '');
      }
    } catch (e) {
      setMarketError('Market analysis unavailable: ' + e.message);
      // Still populate city/state from ZIP table so the page isn't completely blank
      if (zr) { setMarketCity(zr.name?.split(',')[0] || ''); setMarketState(zr.state || ''); }
    }
    setMarketLoading(false);
  }

  // ── Load existing quote ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!id || id === 'new' || configLoading) return;
    supabase.from('quotes').select('*').eq('id', id).single().then(({ data }) => {
      if (!data) return;
      setExistingQuote(data);
      setQuoteStatus(data.status || 'draft');
      setProposalName(data.inputs?.proposalName || '');
      setRecipientBiz(data.inputs?.recipientBiz || data.client_name || '');
      setRecipientContact(data.inputs?.recipientContact || '');
      setRecipientEmail(data.inputs?.recipientEmail || '');
      setRecipientAddress(data.inputs?.recipientAddress || '');
      setRecipientPhone(data.inputs?.recipientPhone || '');
      setRecipientMobilePhone(data.inputs?.recipientMobilePhone || '');
      setHubContactId(data.inputs?.hubspotContactId || '');
      setHubCompanyId(data.inputs?.hubspotCompanyId || '');
      setHubspotSnapshot(data.inputs?.hubspotSnapshot || null);
      setNotificationCc(data.inputs?.notificationCc || '');
      setAddendums(Array.isArray(data.inputs?.addendums) ? data.inputs.addendums : []);
      setEffectiveDate(data.inputs?.effectiveDate || '');
      setClientZip(data.client_zip || '');
      setMarketCity(data.inputs?.marketCity || '');
      setMarketState(data.inputs?.marketState || '');
      setPrepayHours(data.inputs?.prepayHours ?? 2);
      setOverridePrepay(data.inputs?.overridePrepay || false);
      setPrepayOverride(data.inputs?.prepayOverride || '');
      setNotes(data.inputs?.notes || '');
      setHubDealId(data.hubspot_deal_id || '');
      // Backwards-compatibility for FlexIT quotes saved before v3.5.10:
      // the previous onConnect handler pulled the URL from full.deal.dealurl
      // (wrong path) which was always undefined → persisted as null. If we
      // have a deal_id but no URL, derive the URL using the same portal-ID
      // fallback pattern that createDeal() in lib/hubspot.js uses. This
      // self-heals old quotes without forcing reps to reconnect manually —
      // and on next save, the persisted URL becomes the right value.
      if (data.hubspot_deal_url) {
        setHubDealUrl(data.hubspot_deal_url);
      } else if (data.hubspot_deal_id) {
        setHubDealUrl(`https://app.hubspot.com/contacts/47514592/record/0-3/${data.hubspot_deal_id}`);
      } else {
        setHubDealUrl('');
      }
      setHubDealName(data.inputs?.hubspotDealName || '');
      setHubDescription(data.inputs?.hubDescription || '');
      if (data.rep_id) setRepId(data.rep_id);
      if (data.spt_proposal_id) setSptProposalId(data.spt_proposal_id);
      if (data.inputs?.flexHours) setFlexHours(data.inputs.flexHours);
      // Load market analysis
      if (data.client_zip) handleZipChange(data.client_zip, { readOnly: true });
    });
  }, [id, configLoading]);

  // ── Save ─────────────────────────────────────────────────────────────────────
  async function save() {
    // v3.5.99.4: block save if any addendum has unfilled required placeholders.
    // See QuotePage canonical block for rationale.
    const addendumErrors = validateAddendums(addendums, addendumTemplates);
    if (addendumErrors.length > 0) {
      const summary = addendumErrors.slice(0, 3).map(e => `• ${e.template_name}: ${e.placeholder_label || e.reason}`).join('\n');
      const more = addendumErrors.length > 3 ? `\n…and ${addendumErrors.length - 3} more.` : '';
      alert(`Can't save — ${addendumErrors.length} addendum issue${addendumErrors.length === 1 ? '' : 's'}:\n\n${summary}${more}\n\nOpen Special Terms to fix or remove the addendum.`);
      return;
    }
    // v3.5.67: read-only gate — see QuotePage saveQuote for rationale.
    if (isReadOnly) {
      setSaveMsg('You don\'t have permission to save FlexIT quotes. This quote is view-only.');
      return;
    }
    // v3.9.9.2: post-signature lock — a signed agreement freezes terms; persist ONLY notes
    // (FlexIT stores its note as inputs.hubDescription; merge it, don't rewrite inputs).
    if (isQuoteLocked(existingQuote)) {
      setSaving(true); setSaveMsg('');
      try {
        if (existingQuote?.id) {
          await supabase.from('quotes').update({ inputs: { ...(existingQuote?.inputs || {}), hubDescription } }).eq('id', existingQuote.id);
          if (hubDealId && hubDescription) { try { await updateDealDescription(hubDealId, hubDescription); } catch { /* non-blocking */ } }
        }
        setSaveMsg('🔒 Locked — only internal notes were saved.');
      } catch (e) { setSaveMsg('✗ ' + (e?.message || 'Save failed')); }
      setSaving(false);
      return;
    }
    setSaving(true); setSaveMsg('');
    // v3.5.52: spread existing inputs FIRST so we preserve any fields not
    // explicitly listed here. Most importantly: signwellDocuments. Without
    // this, every save after a SignWell document was sent would wipe the
    // signwellDocuments tracking from the DB — the document would still
    // exist in SignWell's system, but the FlexITSignatureCard UI would
    // think no document was ever sent. Same protection applies to any
    // future fields stored in inputs that aren't in the standard form
    // (LOA tracking, IntlWaiver tracking, etc).
    const allInputs = {
      ...(existingQuote?.inputs || {}),
      proposalName, recipientBiz, recipientContact, recipientEmail, recipientAddress, recipientPhone, recipientMobilePhone, hubspotContactId: hubContactId || null, hubspotCompanyId: hubCompanyId || null, hubspotSnapshot, notificationCc, addendums, flexHours: flexHours || null,
      // v3.5.99.7.24: persist back-dated agreement effective date
      effectiveDate: effectiveDate || null,
      marketCity, marketState, prepayHours, overridePrepay, prepayOverride, notes,
      hubspotDealName: hubDealName, hubDescription,
    };
    // v3.5.92.2: totals construction migrated to src/lib/quoteTotals.js
    // (Phase 1 item 2, sub-deploy 3 of 4). Identity persistence — the same
    // object now flows into BOTH the payload.totals AND the saveQuoteVersion
    // call below, eliminating the §3.2 mismatch (pre-deploy payload.totals
    // had 11 fields; the version snapshot had 3 with a literal finalMRR: 0).
    //
    // Net new persisted fields on FlexIT saves:
    //   • Universal: finalMRR (0), effectiveMRR (0), contractValue (= nrc),
    //     nrc (= flexitRevenue), the all-zero hardware/activation/porting
    //     NRC parity fields, the all-null incentive metadata.
    //   • Renamed: gm → impliedGM (under the §1 GM-naming convergence),
    //     laborCost → totalCost, prepayAmount / remoteRate / prepayHours /
    //     burdenedHourlyRate / flexitLaborHours moved into the flexit:
    //     {...} bucket per the locked spec.
    //   • Dropped: marketTier-in-totals duplicate (top-level market_tier
    //     column has been the source of truth since v3.5.62 — totals copy
    //     was redundant). Removed in v3.5.92.3 cleanup deploy along with
    //     buildSignatureDocument fallbacks.
    const totals = buildTotals('flexit', {
      prepayAmount,
      prepayHours,
      flexBlock,
      flexitRevenue,
      flexitLaborHours,
      flexitLaborCost,
      flexitImpliedGM,
      remoteRate,
      burdenedHourlyRate,
    }, {});
    const payload = {
      client_name:     recipientBiz,
      client_zip:      clientZip || null,
      // v3.5.62: persist market_tier at the top level (parity with Managed IT,
      // Bundle, Voice, MultiSite) so the QuotesPage list reads q.market_tier
      // and renders the Market column for FlexIT quotes. Previously the tier
      // was only nested in totals.marketTier, which the list view doesn't
      // read, so FlexIT rows showed a blank Market cell.
      market_tier:     marketAnalysis?.market_tier || null,
      status:          quoteStatus,
      package_name:    'FlexIT On-Demand',
      // v3.5.91.0: dual-write quote_type. Canonical key 'flexit' matches
      // src/lib/quotePermissions.js QUOTE_TYPES + profiles.allowed_quote_types
      // + RLS. v3.5.91.2: column is now the SOLE type discriminator.
      quote_type:      'flexit',
      inputs:          allInputs,
      totals,
      line_items:      [],
      hubspot_deal_id: hubDealId || null,
      hubspot_deal_url:hubDealUrl || null,
      rep_id:          repId || profile?.id || null,
      spt_proposal_id: sptProposalId || null,
      updated_by:      profile?.id,
    };

    try {
      let qId = existingQuote?.id;
      let isNewQuote = false;
      if (!qId) {
        isNewQuote = true;
        payload.created_by = profile?.id;
        const { data, error } = await supabase.from('quotes').insert(payload).select().single();
        if (error) throw error;
        setExistingQuote(data);
        qId = data.id;
        navigate(`/flexIT/${data.id}`, { replace: true });
      } else {
        const { error } = await supabase.from('quotes').update(payload).eq('id', qId);
        if (error) throw error;
      }

      // ── HubSpot sync (parity with QuotePage) ──
      // Description push fires on every save, not just first. Also runs on
      // updates to existing quotes, fixing the silent-no-op bug where edits
      // never reached HubSpot.
      if (hubDealId) {
        // 1. Push deal description if the field has content
        if (hubDescription) {
          try {
            await updateDealDescription(hubDealId, hubDescription);
          } catch (err) {
            console.warn('HubSpot description sync failed:', err.message);
          }
        }
        // 2. Push deal-level totals (MRR is N/A for FlexIT but contract value = prepay)
        try {
          await updateDeal(hubDealId, {
            mrr: 0,
            contractValue: prepayAmount || 0,
            packageName: 'FlexIT On-Demand',
            quoteNumber: existingQuote?.quote_number || (isNewQuote ? 'DRAFT' : ''),
            contractTerm: 0,
          });
        } catch (err) {
          console.warn('HubSpot deal totals sync failed:', err.message);
        }
        // 3. Write quote URL back to HubSpot deal if a field is configured
        try {
          const { data: fd } = await supabase.from('pricing_settings').select('value').eq('key','hubspot_quote_url_field').single();
          if (fd?.value) await writeQuoteUrlToDeal(hubDealId, `${window.location.origin}/flexIT/${qId}`, fd.value);
        } catch(e) { console.warn('HubSpot URL write failed:', e.message); }
      }

      // v3.5.92.2: identity persistence — snapshot uses the SAME `totals`
      // object as the upsert payload above. Pre-deploy this site passed a
      // 3-field subset (finalMRR/onboarding/gm) — and notably the live
      // save had no finalMRR field at all, so the snapshot's literal
      // `finalMRR: 0` was the only place that field existed (§3.2). Fixed
      // by construction.
      await saveQuoteVersion({
        quoteId: qId,
        quoteData: { client_name: recipientBiz, status: quoteStatus },
        inputs: allInputs,
        totals,
        lineItems: [],
        profile,
      });
      setSaveMsg('✓ Saved');
    // v3.5.99.7.4: gap-fill push to HubSpot. Fire-and-forget after save
    // success — pushes only fields where HubSpot was empty when connectDeal
    // pulled (per hubspotSnapshot). Errors logged but don't block UI.
    // The manual full-overwrite push button comes in v3.5.99.7.5.
    if ((hubContactId || hubCompanyId) && hubspotSnapshot) {
      pushHubspotGapFill({
        contactId: hubContactId, companyId: hubCompanyId, snapshot: hubspotSnapshot,
        recipientContact, recipientEmail, recipientPhone, recipientMobilePhone, recipientAddress,
      }).then(r => {
        if (r.updatedSnapshot) setHubspotSnapshot(r.updatedSnapshot);
        if (r.errors?.length) console.warn('HubSpot gap-fill errors:', r.errors);
      }).catch(e => console.warn('HubSpot gap-fill failed:', e));
    }
      setTimeout(() => setSaveMsg(''), 2500);
    } catch (e) {
      setSaveMsg('✗ ' + e.message);
    }
    setSaving(false);
  }

  if (configLoading) return <div style={{ padding:24, color:'#6b7280', fontSize:12 }}>Loading...</div>;

  const SECTION_COLOR = '#f97316'; // FlexIT orange

  // ─── v3.8.9 Unified-UX workspace (flag ferrumiq_v3_flexit, DEFAULT OFF) ───
  // Last of the 5 builders. Legacy two-pane return below is untouched; v3 reuses
  // every handler + child component + verbatim <Sec> blocks + FlexTimeSelector +
  // FlexITDocumentsPanel. FlexIT orange (#f97316) is CONTAINED to the block/
  // prepay panes; the shell (nav + rail + header) uses cobalt (drift §1). No ETF
  // (removed from FlexIT — not reintroduced). sptFlexIT/quoteTotals unchanged.
  const V3_FLEX = (() => {
    try {
      if (typeof window !== 'undefined') {
        const q = new URLSearchParams(window.location.search);
        if (q.get('v3') === '1') return true;
        if (q.get('v3') === '0') return false;
        const stored = window.localStorage.getItem('ferrumiq_v3_flexit');
        if (stored === '1') return true;
        if (stored === '0') return false;                     // persistent opt-out
        return true;   // v3.9.4 — FlexIT workspace is now the DEFAULT (Shaun 2026-08-04).
      }                //          Classic layout reachable via ?v3=0 / stored '0'.
    } catch (e) { /* disabled storage */ }
    return true;
  })();

  if (V3_FLEX) {
    const activeSection = flexitFocusedSection || 'block';
    const statusVariant = ({ won: 'green', approved: 'green', sent: 'blue', lost: 'red', expired: 'red', in_review: 'amber', draft: 'amber' })[quoteStatus] || 'gray';
    const t2 = (t) => (<h2 style={{ fontSize: v3.font.size.section, fontWeight: 600, letterSpacing: '-0.5px', margin: 0 }}>{t}</h2>);
    const s2 = (t) => (<p style={{ fontSize: v3.font.size.body, color: v3.color.muted, marginTop: 6, marginBottom: 4 }}>{t}</p>);
    const ORANGE = v3.color.flexitOrange;

    const v3Sections = [
      { id: 'client',     label: 'Client',        value: recipientBiz || 'Not set', status: recipientBiz ? 'ok' : 'empty' },
      { id: 'market',     label: 'Market & rates',value: marketCity ? `${marketCity} · ${fmt$2(remoteRate)}/hr` : 'Enter ZIP', status: marketCity ? 'ok' : 'empty' },
      { id: 'block',      label: 'Flex block',    value: hasFlexBlock ? `${flexHours}-hr block · ${fmt$2(flexBlock?.blockPrice)}` : 'T&M only', status: 'ok' },
      { id: 'prepay',     label: 'Initial prepayment', value: hasFlexBlock ? 'Replaced by block' : `${prepayHours}hr · ${fmt$2(prepayAmount)}`, status: hasFlexBlock ? 'faint' : 'ok' },
      { id: 'documents',  label: 'Documents & pay', value: existingQuote?.id ? 'Rate sheet · sign · pay' : 'Save first', status: 'ok' },
      { id: 'notes',      label: 'Notes',         value: notes ? notes.slice(0, 32) : '—', status: 'ok' },
      { id: 'engagement', label: 'Engagement',    value: existingQuote?.id ? 'View activity' : 'Not sent', status: 'ok' },
      { id: 'history',    label: 'History',       value: existingQuote?.id ? 'Versions' : '—', status: existingQuote?.id ? 'ok' : 'empty' },
    ];

    const v3Header = (
      <QuoteHeader id={existingQuote?.quote_number || 'New FlexIT quote'} title={proposalName || recipientBiz || 'FlexIT On-Demand quote'} status={quoteStatus} statusVariant={statusVariant}
        extraPills={[{ variant: 'blue', label: 'FlexIT · On-Demand' }, ...(hubDealId ? [{ variant: 'green', label: 'HubSpot linked' }] : [])]} savedLabel={saving ? 'Saving…' : (saveMsg || undefined)}>
        <V3Button variant="secondary" size="sm" onClick={() => setSoPreviewModalOpen(true)} disabled={!existingQuote?.id}>Preview SO</V3Button>
        <V3Button variant="dark" size="sm" onClick={save} disabled={saving || isReadOnly}>{saving ? 'Saving…' : 'Save'}</V3Button>
      </QuoteHeader>
    );

    const v3Rail = (
      <>
        <LiveTotalCard label={hasFlexBlock ? 'Flex block (upfront)' : 'Initial prepayment'} headline={fmt$2(flexitRevenue)} headlineSub={hasFlexBlock ? `${flexHours}hr` : 'prepay'}
          rows={[
            { label: 'Remote rate', value: `${fmt$2(remoteRate)}/hr` },
            { label: 'Labor hours', value: String(flexitLaborHours) },
            { label: 'Labor cost',  value: fmt$2(flexitLaborCost) },
            ...(flexitImpliedGM != null ? [{ label: 'Implied GM', value: fmtPct(flexitImpliedGM), color: v3.color.moneyPositive }] : []),
          ]} />
        <RailCard title="Terms">
          <div style={{ fontSize: 13, color: v3.color.bodySoft }}>Month-to-month · Time &amp; Materials</div>
          <div style={{ fontSize: 12, color: v3.color.muted, marginTop: 4 }}>No monthly recurring fee. Invoiced due upon receipt.</div>
        </RailCard>
      </>
    );

    const renderFlexFocus = () => {
      switch (activeSection) {
        case 'client':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Client')}{s2('Recipient and linked HubSpot deal.')}
            <HubSpotConnect dealId={hubDealId} dealUrl={hubDealUrl} dealName={hubDealName} description={hubDescription} onDescriptionChange={setHubDescription} quoteNumber={existingQuote?.quote_number} mrr={0} contractValue={prepayAmount} packageName="FlexIT On-Demand" contractTerm={null} existingQuoteId={existingQuote?.id} clientName={recipientBiz}
              onConnect={full => { setHubDealId(full.dealId); setHubDealUrl(full.dealUrl || ''); setHubDealName(full.deal.dealname || ''); if (full.company) { if (full.company.name) setRecipientBiz(full.company.name); const addr = [full.company.address, full.company.address2, full.company.city, full.company.state, full.company.zip].filter(Boolean).join(', '); if (addr) setRecipientAddress(addr); if (full.company.zip) handleZipChange(full.company.zip); } else { const extracted = full.deal.dealname?.split(/\s[-–—]\s/)?.[0]?.trim(); if (extracted) setRecipientBiz(extracted); } if (full.contact) { const name = [full.contact.firstname, full.contact.lastname].filter(Boolean).join(' '); if (name) setRecipientContact(name); if (full.contact.email) setRecipientEmail(full.contact.email); if (full.contact.phone) setRecipientPhone(full.contact.phone); if (full.contact.mobilephone) setRecipientMobilePhone(full.contact.mobilephone); if (full.contactId) setHubContactId(full.contactId); if (full.companyId) setHubCompanyId(full.companyId); setHubspotSnapshot(captureHubspotSnapshot(full)); } if (!proposalName && full.deal.dealname) setProposalName(`FerrumIT On-Demand IT Support — ${full.company?.name || full.deal.dealname}`); }}
              onDisconnect={() => { setHubDealId(''); setHubDealUrl(''); setHubDealName(''); }} />
            <SPTConnect proposalId={sptProposalId} quoteId={existingQuote?.id} clientName={recipientBiz} quoteNumber={existingQuote?.quote_number} settings={settings} quoteTypeLabel="On-Demand IT Support" onConnect={(pid) => setSptProposalId(pid)} onDisconnect={() => setSptProposalId(null)}
              customCreate={async ({ sptApiKey, name }) => { if (!rateSheet) { throw new Error('Enter a ZIP code first to compute market-adjusted rates'); } const quote = buildFlexITQuoteShape({ proposalName: name, recipientBiz, recipientContact, recipientEmail, recipientAddress, recipientPhone, recipientMobilePhone, marketCity, marketState, prepayHours, prepayAmount, remoteRate, flexHours, flexBlock, quoteNumber: existingQuote?.quote_number }); return createFlexITSPTProposal({ quote, rateSheet, settings, sptApiKey }); }} />
            <SubCard eyebrow="Proposal Details">
              <Fld lbl="Assigned Sales Rep"><RepSelector repId={repId} setRepId={setRepId} teamMembers={teamMembers} /></Fld>
              <Fld lbl="Proposal Name"><TI value={proposalName} onChange={setProposalName} placeholder="FlexIT — Acme Corp"/></Fld>
              <Fld lbl="Client Business Name"><TI value={recipientBiz} onChange={setRecipientBiz} placeholder="Acme Corp"/></Fld>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}><Fld lbl="Contact Name"><TI value={recipientContact} onChange={setRecipientContact} placeholder="Jane Smith"/></Fld><Fld lbl="Contact Email"><TI value={recipientEmail} onChange={setRecipientEmail} placeholder="jane@acme.com"/></Fld></div>
              <Fld lbl="Business Address"><TI value={recipientAddress} onChange={setRecipientAddress} placeholder="123 Main St"/></Fld>
              <Grid2><Fld lbl="Office Phone"><TI value={recipientPhone} onChange={setRecipientPhone} placeholder="(555) 123-4567"/></Fld><Fld lbl="Mobile Phone"><TI value={recipientMobilePhone} onChange={setRecipientMobilePhone} placeholder="(555) 765-4321"/></Fld></Grid2>
              {(hubContactId || hubCompanyId) && (<div style={{ marginTop: 8 }}><V3Button variant="secondary" size="sm" onClick={pushAllToHubspot} disabled={hubPushing}>{hubPushing ? '⏳ Pushing…' : '↑ Push to HubSpot'}</V3Button>{hubPushMsg && <span style={{ fontSize: 11, color: v3.color.muted, marginLeft: 8 }}>{hubPushMsg}</span>}</div>)}
              <div style={{ marginTop: 10 }}><Fld lbl="CC on Approval (comma-separated)"><TI value={notificationCc} onChange={setNotificationCc} placeholder="alice@ferrumit.com, bob@ferrumit.com"/></Fld></div>
              <Fld lbl="Quote Status" s={{ marginTop:8 }}><SI v={quoteStatus} s={setQuoteStatus} opts={[['draft','Draft'],['in_review','In Review'],['approved','Approved'],['sent','Sent'],['won','Won'],['lost','Lost'],['expired','Expired']]}/></Fld>
            </SubCard></div>);
        case 'market':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Market & rates')}{s2('The client ZIP drives the market-adjusted remote T&M rate and the full rate card.')}
            <SubCard eyebrow="Market">
              <Fld lbl="Zip Code"><input value={clientZip} onChange={e => handleZipChange(e.target.value)} placeholder="60601" style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace', outline:'none' }}/>{marketCity && <div style={{ fontSize:9, color:'#6b7280', marginTop:2 }}>{marketCity}, {marketState}</div>}</Fld>
            </SubCard>
            <MarketRateCard quoteId={existingQuote?.id} clientZip={clientZip}
              onRatesAccepted={(rates, suggestedTier, analysis) => { if (rates) setAcceptedRates(rates); if (analysis) { setMarketAnalysis({ ...analysis, rates: { ...analysis.rates, ...rates } }); if (analysis.city) setMarketCity(analysis.city); if (analysis.state) setMarketState(analysis.state); } else if (rates) { setMarketAnalysis(prev => prev ? { ...prev, rates: { ...prev.rates, ...rates } } : prev); } }} />
            <div style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:6, padding:12 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#0f1e3c', marginBottom:10 }}>Rate Card Preview</div>
              {!marketAnalysis ? (<div style={{ fontSize:11, padding:'12px 0', textAlign:'center', color: marketError ? '#dc2626' : '#9ca3af' }}>{marketLoading ? '⏳ Analyzing market rates for ' + (clientZip || 'this ZIP') + '...' : marketError ? '✗ ' + marketError : clientZip && clientZip.length === 5 ? 'No market analysis available yet for ZIP ' + clientZip + '.' : 'Enter a ZIP code to see market-adjusted rates'}</div>) : (rateSheet?.sections.map(section => (<div key={section.id} style={{ marginBottom:10 }}><div style={{ fontSize:9, fontWeight:700, color: ORANGE, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:4 }}>{section.title}</div><table style={{ width:'100%', borderCollapse:'collapse', fontSize:10 }}><tbody>{section.items.map((item, i) => (<tr key={i} style={{ borderBottom:'1px solid #f9fafb' }}><td style={{ padding:'3px 0', color:'#374151' }}>{item.service}</td><td style={{ padding:'3px 0', textAlign:'right', fontFamily:'DM Mono, monospace', color:'#0f1e3c', fontWeight:500 }}>{fmtRate(item)}</td></tr>))}</tbody></table></div>)))}
            </div>
          </div>);
        case 'block':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Flex block')}{s2('Optionally pre-purchase a discounted block of hours. The block becomes the upfront fee (replacing the prepayment).')}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
              {[hasFlexBlock ? [`Flex Block (${flexHours}hr)`, fmt$2(flexBlock?.blockPrice), '#c2410c', '#fff7ed'] : ['Initial Prepayment', fmt$2(prepayAmount), '#c2410c', '#fff7ed'], ['Remote Rate', `${fmt$2(remoteRate)}/hr`, '#0f766e', '#f0fdf4'], ['Implied GM', flexitImpliedGM == null ? '—' : fmtPct(flexitImpliedGM), flexitImpliedGM == null ? '#9ca3af' : gmColor(flexitImpliedGM), flexitImpliedGM == null ? '#f9fafb' : gmBg(flexitImpliedGM)]].map(([l,val,co,bg]) => (<div key={l} style={{ background:bg, borderRadius:6, padding:'9px 11px' }}><div style={{ fontSize:9, color:'#6b7280', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:2 }}>{l}</div><div style={{ fontSize:13, fontWeight:700, fontFamily:'DM Mono, monospace', color:co, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{val}</div></div>))}
            </div>
            {flexitImpliedGM != null && flexitRevenue > 0 && (<div style={{ fontSize:10, color:'#6b7280', lineHeight:1.6 }}>GM = ({fmt$2(flexitRevenue)} revenue − {flexitLaborHours}hr × {fmt$2(burdenedHourlyRate)}/hr burdened = {fmt$2(flexitLaborCost)} cost) ÷ {fmt$2(flexitRevenue)} = <strong style={{ color: gmColor(flexitImpliedGM) }}>{fmtPct(flexitImpliedGM)}</strong></div>)}
            <FlexTimeSelector remoteRate={remoteRate} settings={settings} selectedHours={flexHours} onChange={hrs => setFlexHours(hrs)} mode="ondemand" packageModel="none" />
            <div style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:6, padding:12 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#0f1e3c', marginBottom:8 }}>Billing Summary</div>
              <table style={{ width:'100%', borderCollapse:'collapse', border:'1px solid #e5e7eb', borderRadius:4, overflow:'hidden' }}>
                <thead><tr style={{ background:'#0f1e3c' }}><th style={{ padding:'8px 12px', fontSize:10, fontWeight:700, color:'white', textAlign:'left', textTransform:'uppercase', letterSpacing:'.04em' }}>Payment</th><th style={{ padding:'8px 12px', fontSize:10, fontWeight:700, color:'white', textAlign:'left', textTransform:'uppercase', letterSpacing:'.04em' }}>Amount</th><th style={{ padding:'8px 12px', fontSize:10, fontWeight:700, color:'white', textAlign:'left', textTransform:'uppercase', letterSpacing:'.04em' }}>Trigger</th></tr></thead>
                <tbody>
                  {hasFlexBlock ? (<tr style={{ borderBottom:'1px solid #fde68a', background:'#fffbeb' }}><td style={{ padding:'10px 12px', fontSize:11, fontWeight:700, color:'#c2410c' }}>#1 — Flex Block — {flexHours}hrs pre-purchased</td><td style={{ padding:'10px 12px', fontSize:12, fontFamily:'DM Mono, monospace', fontWeight:700, color:'#c2410c' }}>{fmt$2(flexBlock?.blockPrice)}</td><td style={{ padding:'10px 12px', fontSize:11, color:'#6b7280' }}>Due in full upon agreement signing — non-refundable · Valid 12 months · refillable at this rate</td></tr>) : (<tr style={{ borderBottom:'1px solid #fde68a', background:'#fffbeb' }}><td style={{ padding:'10px 12px', fontSize:11, fontWeight:700, color:'#374151' }}>#1 — Initial Prepayment</td><td style={{ padding:'10px 12px', fontSize:12, fontFamily:'DM Mono, monospace', fontWeight:700, color:'#c2410c' }}>{fmt$2(prepayAmount)}</td><td style={{ padding:'10px 12px', fontSize:11, color:'#6b7280' }}>Upon agreement signing — non-refundable · Applied toward first engagement</td></tr>)}
                  <tr><td style={{ padding:'10px 12px', fontSize:11, fontWeight:700, color:'#374151' }}>Ongoing Labor</td><td style={{ padding:'10px 12px', fontSize:11, color:'#6b7280' }}>At published rates</td><td style={{ padding:'10px 12px', fontSize:11, color:'#6b7280' }}>{hasFlexBlock ? 'After flex block depleted — billed as consumed' : 'Billed as consumed — invoiced upon completion or end of billing period'}</td></tr>
                </tbody>
              </table>
              <div style={{ fontSize:9, color:'#9ca3af', marginTop:8, lineHeight:1.5 }}>Term: Month to Month · Invoicing: Due Upon Receipt · No monthly recurring fee</div>
            </div>
          </div>);
        case 'prepay':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Initial prepayment')}{s2('A prepayment collected before services begin, applied toward the first engagement.')}
            {hasFlexBlock ? (
              <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius: v3.radius.card, padding:16 }}><div style={{ fontSize: v3.font.size.label, fontWeight:700, color:'#c2410c', marginBottom:4 }}>Replaced by the flex block</div><div style={{ fontSize: v3.font.size.small, color:'#92400e', lineHeight:1.5 }}>The {flexHours}hr flex block is the upfront fee for this engagement — no separate prepayment is required. Remove the block (in Flex block) to switch back to a prepayment.</div></div>
            ) : (
              <SubCard eyebrow="New Account Prepayment">
                <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:5, padding:'8px 10px', marginBottom:8 }}><div style={{ fontSize:10, fontWeight:700, color:'#c2410c', marginBottom:4 }}>New Account Prepayment</div><div style={{ fontSize:9, color:'#92400e', lineHeight:1.5 }}>A prepayment is required prior to any services being rendered. Applied toward the first engagement. Selecting a flex block replaces this with the block fee.</div></div>
                <Fld lbl="Prepayment Hours"><NI v={prepayHours} s={v => { setPrepayHours(v); setOverridePrepay(false); }}/></Fld>
                <Fld lbl="Prepayment Amount"><div style={{ display:'flex', gap:4, alignItems:'center' }}>{overridePrepay ? <input value={prepayOverride} onChange={e => setPrepayOverride(e.target.value)} style={{ flex:1, padding:'4px 6px', border:`1px solid ${ORANGE}`, borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace', fontWeight:700, color:'#c2410c', outline:'none' }}/> : <div style={{ flex:1, padding:'4px 6px', background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:4, fontSize:12, fontFamily:'DM Mono, monospace', fontWeight:700, color:'#c2410c' }}>{fmt$2(prepayAmount)}</div>}<button onClick={() => setOverridePrepay(v => !v)} style={{ padding:'3px 7px', fontSize:9, background:'white', border:'1px solid #d1d5db', borderRadius:3, cursor:'pointer', color:'#6b7280' }}>{overridePrepay ? 'Auto' : 'Edit'}</button></div><div style={{ fontSize:9, color:'#9ca3af', marginTop:2 }}>Auto: {prepayHours}hr × {fmt$2(remoteRate)}/hr{marketAnalysis ? ' · market-adjusted' : ' · base rate (enter zip for market rate)'}</div></Fld>
              </SubCard>
            )}
          </div>);
        case 'documents':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Documents & pay')}{s2('Rate sheet, special terms, effective date, e-signature, and prepayment.')}
            <QuoteDocuments quoteId={existingQuote?.id} />
            <FlexITDocumentsPanel analysis={marketAnalysis} settings={settings} clientName={recipientBiz} recipientContact={recipientContact} quoteId={existingQuote?.id} quoteNumber={existingQuote?.quote_number} quoteStatus={quoteStatus} sptProposalId={sptProposalId} onSPTLinked={(pid) => setSptProposalId(pid)} prepayAmount={prepayAmount} remoteRate={remoteRate} flexHours={flexHours} flexBlockPrice={flexBlock?.blockPrice || 0} hasFlexBlock={hasFlexBlock} clientEmail={recipientEmail} hubspotDealId={hubDealId} userId={profile?.id} onOpenAddendums={() => setAddendumsModalOpen(true)} addendumsCount={addendums.length} onOpenServiceOrderPreview={() => setSoPreviewModalOpen(true)} effectiveDate={effectiveDate} setEffectiveDate={setEffectiveDate} />
          </div>);
        case 'notes':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Notes')}{s2('Internal context and the assumptions shown on the agreement.')}
            <SubCard eyebrow="Internal Notes"><Fld lbl="Notes"><textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Notes about this client or engagement..." style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, resize:'vertical', outline:'none' }}/></Fld></SubCard>
            <div style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:6, padding:12 }}><div style={{ fontSize:11, fontWeight:700, color:'#0f1e3c', marginBottom:8 }}>Assumptions</div>{FLEXIT_ASSUMPTIONS.map((a, i) => (<div key={i} style={{ display:'flex', gap:6, marginBottom:4 }}><span style={{ color: ORANGE, fontSize:10, flexShrink:0, marginTop:1 }}>•</span><span style={{ fontSize:10, color:'#374151', lineHeight:1.6 }}>{a}</span></div>))}</div>
            <QuoteNotes quoteId={existingQuote?.id} quoteNumber={existingQuote?.quote_number} clientName={recipientBiz} hubDealId={hubDealId} />
          </div>);
        case 'engagement':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('Engagement')}{s2('Client interaction with the sent quote (live).')}<EngagementPanel quoteId={existingQuote?.id} /></div>);
        case 'history':
          return (<div style={{ display: 'flex', flexDirection: 'column', gap: v3.space.xl }}>{t2('History')}{s2('Saved versions of this quote.')}<QuoteHistory quoteId={existingQuote?.id} /></div>);
        default:
          return null;
      }
    };

    const locked = isQuoteLocked(existingQuote);
    return (
      <div style={{ height: '100%', overflowY: 'auto' }}>
        {isReadOnly && (<div style={{ padding: '8px 24px', background: v3.color.status.amberBg, color: v3.color.status.amberText, fontSize: 12, fontWeight: 600 }}>🔒 View only — your account no longer has permission to edit FlexIT quotes.</div>)}
        {locked && (
          <LockedQuoteBanner notes={hubDescription} onNotesChange={setHubDescription} onSave={save} saving={saving} reason={QUOTE_LOCK_REASON} />
        )}
        <WorkspaceShell header={v3Header} sections={v3Sections} activeId={activeSection} onSelect={setFlexitFocusedSection} rail={v3Rail}>
          {locked && activeSection !== 'documents'
            ? <div style={{ pointerEvents: 'none', opacity: 0.55 }} aria-disabled="true">{renderFlexFocus()}</div>
            : renderFlexFocus()}
        </WorkspaceShell>
        <AddendumsModal open={addendumsModalOpen} onClose={() => setAddendumsModalOpen(false)} addendums={addendums} templates={addendumTemplates} onSave={setAddendums} />
        <ServiceOrderPreviewModal open={soPreviewModalOpen} onClose={() => setSoPreviewModalOpen(false)} quoteId={existingQuote?.id} docType="flexit_quote" clientBiz={recipientBiz} />
      </div>
    );
  }

  return (
    <div style={{ display:'flex', height:'100%', overflow:'hidden' }}>

      {/* ── LEFT COLUMN ── */}
      <div style={{ width:345, flexShrink:0, background:'white', borderRight:'1px solid #e5e7eb', overflowY:'auto', padding:'12px 14px' }}>

        {/* v3.5.67: read-only banner */}
        {isReadOnly && (
          <div style={{ marginBottom:8, padding:'8px 10px', background:'#fef3c7', border:'1px solid #fde68a', borderRadius:5, fontSize:11, color:'#92400e', fontWeight:600 }}>
            🔒 View only — your account no longer has permission to edit FlexIT quotes. Contact an admin to restore access.
          </div>
        )}

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, padding:'8px 10px', background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:6 }}>
          <div style={{ width:28, height:28, background:'#f97316', borderRadius:5, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <span style={{ color:'white', fontSize:14, fontWeight:700 }}>⚡</span>
          </div>
          <div>
            <div style={{ fontSize:12, fontWeight:700, color:'#c2410c' }}>FlexIT On-Demand</div>
            <div style={{ fontSize:9, color:'#9ca3af' }}>Time & Materials — Break/Fix</div>
          </div>
        </div>

        {/* HubSpot */}
        <HubSpotConnect
          dealId={hubDealId} dealUrl={hubDealUrl} dealName={hubDealName}
          description={hubDescription} onDescriptionChange={setHubDescription}
          quoteNumber={existingQuote?.quote_number}
          mrr={0} contractValue={prepayAmount}
          packageName="FlexIT On-Demand" contractTerm={null}
          existingQuoteId={existingQuote?.id} clientName={recipientBiz}
          onConnect={full => {
            setHubDealId(full.dealId);
            setHubDealUrl(full.dealUrl || '');
            setHubDealName(full.deal.dealname || '');
            // Company → recipient business name + address + ZIP-driven market analysis
            if (full.company) {
              if (full.company.name) setRecipientBiz(full.company.name);
              const addr = [full.company.address, full.company.address2, full.company.city, full.company.state, full.company.zip].filter(Boolean).join(', ');
              if (addr) setRecipientAddress(addr);
              if (full.company.zip) handleZipChange(full.company.zip);
            } else {
              // Fallback when deal has no associated company — extract a name from the deal title
              const extracted = full.deal.dealname?.split(/\s[-–—]\s/)?.[0]?.trim();
              if (extracted) setRecipientBiz(extracted);
            }
            // Contact → name + email
            if (full.contact) {
              const name = [full.contact.firstname, full.contact.lastname].filter(Boolean).join(' ');
              if (name) setRecipientContact(name);
              if (full.contact.email) setRecipientEmail(full.contact.email);
              if (full.contact.phone) setRecipientPhone(full.contact.phone);
              if (full.contact.mobilephone) setRecipientMobilePhone(full.contact.mobilephone);
              // v3.5.99.7.4: capture write-back routing IDs + original-state snapshot
              if (full.contactId) setHubContactId(full.contactId);
              if (full.companyId) setHubCompanyId(full.companyId);
              setHubspotSnapshot(captureHubspotSnapshot(full));
            }
            // Auto-suggest a proposal name when one isn't already set
            if (!proposalName && full.deal.dealname) {
              setProposalName(`FerrumIT On-Demand IT Support — ${full.company?.name || full.deal.dealname}`);
            }
          }}
          onDisconnect={() => { setHubDealId(''); setHubDealUrl(''); setHubDealName(''); }}
        />
        <SPTConnect
          proposalId={sptProposalId}
          quoteId={existingQuote?.id}
          clientName={recipientBiz}
          quoteNumber={existingQuote?.quote_number}
          settings={settings}
          quoteTypeLabel="On-Demand IT Support"
          onConnect={(pid) => setSptProposalId(pid)}
          onDisconnect={() => setSptProposalId(null)}
          customCreate={async ({ sptApiKey, name }) => {
            if (!rateSheet) {
              throw new Error('Enter a ZIP code first to compute market-adjusted rates');
            }
            const quote = buildFlexITQuoteShape({
              proposalName:     name,
              recipientBiz,
              recipientContact,
              recipientEmail,
              recipientAddress,
              recipientPhone,
              recipientMobilePhone,
              marketCity,
              marketState,
              prepayHours,
              prepayAmount,
              remoteRate,
              flexHours,
              flexBlock,
              quoteNumber: existingQuote?.quote_number,
            });
            return createFlexITSPTProposal({ quote, rateSheet, settings, sptApiKey });
          }}
        />

        {/* Proposal Details */}
        <Sec t="Proposal Details" c="#0f1e3c">
          <Fld lbl="Assigned Sales Rep">
            <RepSelector repId={repId} setRepId={setRepId} teamMembers={teamMembers} />
          </Fld>
          <Fld lbl="Proposal Name"><TI value={proposalName} onChange={setProposalName} placeholder="FlexIT — Acme Corp"/></Fld>
          <Fld lbl="Client Business Name"><TI value={recipientBiz} onChange={setRecipientBiz} placeholder="Acme Corp"/></Fld>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
            <Fld lbl="Contact Name"><TI value={recipientContact} onChange={setRecipientContact} placeholder="Jane Smith"/></Fld>
            <Fld lbl="Contact Email"><TI value={recipientEmail} onChange={setRecipientEmail} placeholder="jane@acme.com"/></Fld>
          </div>
          <Fld lbl="Business Address"><TI value={recipientAddress} onChange={setRecipientAddress} placeholder="123 Main St"/></Fld>
          <Grid2>
            <Fld lbl="Office Phone"><TI value={recipientPhone} onChange={setRecipientPhone} placeholder="(555) 123-4567"/></Fld>
            <Fld lbl="Mobile Phone"><TI value={recipientMobilePhone} onChange={setRecipientMobilePhone} placeholder="(555) 765-4321"/></Fld>
          </Grid2>
          {/* v3.5.99.7.6: manual full-overwrite push to HubSpot */}
          {(hubContactId || hubCompanyId) && (
            <div style={{ marginTop: 6, padding: '6px 10px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={pushAllToHubspot} disabled={hubPushing}
                style={{ padding: '4px 10px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 3, fontSize: 10, fontWeight: 600, cursor: hubPushing ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}>
                {hubPushing ? '⏳ Pushing...' : '↑ Push to HubSpot'}
              </button>
              <span style={{ fontSize: 9, color: '#0c4a6e' }}>Overwrites HubSpot contact + company with values above</span>
              {hubPushMsg && <span style={{ fontSize: 10, color: '#075985', flex: 1, textAlign: 'right', minWidth: 0 }}>{hubPushMsg}</span>}
            </div>
          )}
          {/* v3.5.99.0: Internal Notification CCs — see QuotePage block for rationale */}
          <div style={{ marginTop: 10, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#92400e', marginBottom: 4 }}>🔒 Internal Notifications</div>
            <Fld lbl="CC on Approval (comma-separated)"><TI value={notificationCc} onChange={setNotificationCc} placeholder="alice@ferrumit.com, bob@ferrumit.com"/></Fld>
            <div style={{ fontSize: 9, color: '#92400e', marginTop: 4, lineHeight: 1.4 }}>Leave blank to use the global default from Admin → Notifications. Not visible to the customer.</div>
          </div>
          <Fld lbl="Zip Code">
            <input value={clientZip} onChange={e => handleZipChange(e.target.value)} placeholder="60601"
              style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace', outline:'none' }}/>
            {marketCity && <div style={{ fontSize:9, color:'#6b7280', marginTop:2 }}>{marketCity}, {marketState}</div>}
          </Fld>
          <Fld lbl="Quote Status" s={{ marginTop:8 }}>
            <SI v={quoteStatus} s={setQuoteStatus} opts={[['draft','Draft'],['in_review','In Review'],['approved','Approved'],['sent','Sent'],['won','Won'],['lost','Lost'],['expired','Expired']]}/>
          </Fld>
        </Sec>

        {/* FlexIT Service Setup */}
        <Sec t="Service Setup" c={SECTION_COLOR}>
          {hasFlexBlock ? (
            <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:5, padding:'8px 10px', marginBottom:8 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#c2410c', marginBottom:4 }}>Flex Block — Pre-Purchased</div>
              <div style={{ fontSize:9, color:'#92400e', lineHeight:1.5 }}>
                The {flexHours}hr flex block is the upfront fee for this engagement — paid in full upon agreement signing.
                No separate initial prepayment is required when a flex block is selected.
              </div>
            </div>
          ) : (
            <>
              <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:5, padding:'8px 10px', marginBottom:8 }}>
                <div style={{ fontSize:10, fontWeight:700, color:'#c2410c', marginBottom:4 }}>New Account Prepayment</div>
                <div style={{ fontSize:9, color:'#92400e', lineHeight:1.5 }}>
                  A prepayment is required prior to any services being rendered. Applied toward the first engagement.
                  Selecting a flex block below replaces this with the block fee.
                </div>
              </div>
              <Fld lbl="Prepayment Hours">
                <NI v={prepayHours} s={v => { setPrepayHours(v); setOverridePrepay(false); }}/>
              </Fld>
              <Fld lbl="Prepayment Amount">
                <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                  {overridePrepay
                    ? <input value={prepayOverride} onChange={e => setPrepayOverride(e.target.value)}
                        style={{ flex:1, padding:'4px 6px', border:'1px solid #f97316', borderRadius:4, fontSize:11, fontFamily:'DM Mono, monospace', fontWeight:700, color:'#c2410c', outline:'none' }}/>
                    : <div style={{ flex:1, padding:'4px 6px', background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:4, fontSize:12, fontFamily:'DM Mono, monospace', fontWeight:700, color:'#c2410c' }}>
                        {fmt$2(prepayAmount)}
                      </div>
                  }
                  <button onClick={() => setOverridePrepay(v => !v)}
                    style={{ padding:'3px 7px', fontSize:9, background:'white', border:'1px solid #d1d5db', borderRadius:3, cursor:'pointer', color:'#6b7280' }}>
                    {overridePrepay ? 'Auto' : 'Edit'}
                  </button>
                </div>
                <div style={{ fontSize:9, color:'#9ca3af', marginTop:2 }}>
                  Auto: {prepayHours}hr × {fmt$2(remoteRate)}/hr
                  {marketAnalysis ? ' · market-adjusted' : ' · base rate (enter zip for market rate)'}
                </div>
              </Fld>
            </>
          )}
          <Fld lbl="Internal Notes">
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
              placeholder="Notes about this client or engagement..."
              style={{ width:'100%', padding:'4px 6px', border:'1px solid #d1d5db', borderRadius:4, fontSize:11, resize:'vertical', outline:'none' }}/>
          </Fld>
        </Sec>

        {/* Save */}
        <div style={{ marginTop: 8 }}>
          <QuoteSaveBar
            onSave={save}
            existingQuote={existingQuote}
            quoteStatus={quoteStatus}
            setQuoteStatus={setQuoteStatus}
            quoteType="flexIT"
            saving={saving}
            saveMsg={saveMsg}
            saveLabelExisting="Save Changes"
            saveButtonColor="#f97316"
            saveButtonPadding="8px"
            saveButtonRadius={5}
            saveButtonFontSize={12}
            saveButtonFontWeight={700}
            saveMsgPlacement="inline"
            reviewRowMarginTop={8}
            showWrapper={false}
          />
        </div>
      </div>

      {/* ── RIGHT COLUMN ── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', background:'#f8fafc', minWidth:0 }}>
        <ReviewBanner quote={{ ...existingQuote, status: quoteStatus }} quoteType="flexIT" onStatusChange={s => setQuoteStatus(s)} />

        <div style={{ flex:1, overflowY:'auto', padding:'14px 16px' }}>

          {/* Quote number + status badge */}
          {existingQuote && (
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
              <span style={{ fontSize:12, fontFamily:'DM Mono, monospace', color:'#6b7280', fontWeight:600 }}>{existingQuote.quote_number}</span>
              <span style={{ fontSize:10, padding:'2px 7px', borderRadius:3, fontWeight:600,
                background: quoteStatus==='won'?'#dcfce7':quoteStatus==='sent'?'#dbeafe':quoteStatus==='approved'?'#f0fdf4':'#f3f4f6',
                color: quoteStatus==='won'?'#166534':quoteStatus==='sent'?'#1e40af':quoteStatus==='approved'?'#166534':'#6b7280'
              }}>{quoteStatus}</span>
            </div>
          )}

          {/* KPI Strip */}
          {/* v3.5.56: 4-column grid (was 3) — Implied GM tile added so reps
              can see the deal's margin without leaving the page. Tile uses
              the same gmColor/gmBg threshold function managed IT and Bundle
              use, so green/yellow/red mean the same thing across all quote
              types. When revenue is zero (no block, no prepay), GM is null
              and we display "—" instead of a misleading 0%. */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:10, marginBottom:16 }}>
            {[
              hasFlexBlock
                ? [`Flex Block (${flexHours}hr)`, fmt$2(flexBlock?.blockPrice), '#c2410c', '#fff7ed']
                : ['Initial Prepayment', fmt$2(prepayAmount), '#c2410c', '#fff7ed'],
              ['Remote Rate', `${fmt$2(remoteRate)}/hr`, '#0f766e', '#f0fdf4'],
              [
                'Implied GM',
                flexitImpliedGM == null ? '—' : fmtPct(flexitImpliedGM),
                flexitImpliedGM == null ? '#9ca3af' : gmColor(flexitImpliedGM),
                flexitImpliedGM == null ? '#f9fafb' : gmBg(flexitImpliedGM),
              ],
              ['Market', marketAnalysis ? `${marketCity || '—'} · ${marketAnalysis.market_tier}` : 'Enter ZIP', '#6d28d9', '#faf5ff'],
            ].map(([l,v,co,bg]) => (
              <div key={l} style={{ background:bg, borderRadius:6, padding:'9px 11px' }}>
                <div style={{ fontSize:9, color:'#6b7280', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:2 }}>{l}</div>
                <div style={{ fontSize:13, fontWeight:700, fontFamily:'DM Mono, monospace', color:co, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{v}</div>
              </div>
            ))}
          </div>

          {/* v3.5.56: GM breakdown helper — visible only when we have a real
              GM number, gives reps the labor-cost math behind the tile so
              they can trace WHY a margin is what it is. Especially useful
              when a flex block discount is compressing GM significantly. */}
          {flexitImpliedGM != null && flexitRevenue > 0 && (
            <div style={{ fontSize:10, color:'#6b7280', marginTop:-10, marginBottom:14, lineHeight:1.6 }}>
              GM = ({fmt$2(flexitRevenue)} revenue − {flexitLaborHours}hr × {fmt$2(burdenedHourlyRate)}/hr burdened
              = {fmt$2(flexitLaborCost)} cost) ÷ {fmt$2(flexitRevenue)} = <strong style={{ color: gmColor(flexitImpliedGM) }}>{fmtPct(flexitImpliedGM)}</strong>
            </div>
          )}

          {/* Flex Time Block — optional pre-purchase */}
          <div style={{ marginBottom:12 }}>
            <FlexTimeSelector
              remoteRate={remoteRate}
              settings={settings}
              selectedHours={flexHours}
              onChange={hrs => setFlexHours(hrs)}
              mode="ondemand"
              packageModel="none"
            />
          </div>

          {/* FlexIT Billing Summary */}
          <div style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:6, padding:12, marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#0f1e3c', marginBottom:8 }}>Billing Summary</div>
            <table style={{ width:'100%', borderCollapse:'collapse', border:'1px solid #e5e7eb', borderRadius:4, overflow:'hidden' }}>
              <thead>
                <tr style={{ background:'#0f1e3c' }}>
                  <th style={{ padding:'8px 12px', fontSize:10, fontWeight:700, color:'white', textAlign:'left', textTransform:'uppercase', letterSpacing:'.04em' }}>Payment</th>
                  <th style={{ padding:'8px 12px', fontSize:10, fontWeight:700, color:'white', textAlign:'left', textTransform:'uppercase', letterSpacing:'.04em' }}>Amount</th>
                  <th style={{ padding:'8px 12px', fontSize:10, fontWeight:700, color:'white', textAlign:'left', textTransform:'uppercase', letterSpacing:'.04em' }}>Trigger</th>
                </tr>
              </thead>
              <tbody>
                {hasFlexBlock ? (
                  /* Flex block IS the upfront — no separate prepay row. */
                  <tr style={{ borderBottom:'1px solid #fde68a', background:'#fffbeb' }}>
                    <td style={{ padding:'10px 12px', fontSize:11, fontWeight:700, color:'#c2410c' }}>#1 — Flex Block — {flexHours}hrs pre-purchased</td>
                    <td style={{ padding:'10px 12px', fontSize:12, fontFamily:'DM Mono, monospace', fontWeight:700, color:'#c2410c' }}>{fmt$2(flexBlock?.blockPrice)}</td>
                    <td style={{ padding:'10px 12px', fontSize:11, color:'#6b7280' }}>Due in full upon agreement signing — non-refundable · Valid 12 months · refillable at this rate</td>
                  </tr>
                ) : (
                  /* No flex block — standard 2hr prepay for new-account engagement. */
                  <tr style={{ borderBottom:'1px solid #fde68a', background:'#fffbeb' }}>
                    <td style={{ padding:'10px 12px', fontSize:11, fontWeight:700, color:'#374151' }}>#1 — Initial Prepayment</td>
                    <td style={{ padding:'10px 12px', fontSize:12, fontFamily:'DM Mono, monospace', fontWeight:700, color:'#c2410c' }}>{fmt$2(prepayAmount)}</td>
                    <td style={{ padding:'10px 12px', fontSize:11, color:'#6b7280' }}>Upon agreement signing — non-refundable · Applied toward first engagement</td>
                  </tr>
                )}
                <tr>
                  <td style={{ padding:'10px 12px', fontSize:11, fontWeight:700, color:'#374151' }}>Ongoing Labor</td>
                  <td style={{ padding:'10px 12px', fontSize:11, color:'#6b7280' }}>At published rates</td>
                  <td style={{ padding:'10px 12px', fontSize:11, color:'#6b7280' }}>{hasFlexBlock ? 'After flex block depleted — billed as consumed' : 'Billed as consumed — invoiced upon completion or end of billing period'}</td>
                </tr>
              </tbody>
            </table>
            <div style={{ fontSize:9, color:'#9ca3af', marginTop:8, lineHeight:1.5 }}>
              Term: Month to Month · Invoicing: Due Upon Receipt · No monthly recurring fee
            </div>
          </div>

          {/* Market Rate Analysis — full card with accept / refresh / overrides */}
          <MarketRateCard
            quoteId={existingQuote?.id}
            clientZip={clientZip}
            onRatesAccepted={(rates, suggestedTier, analysis) => {
              // Race-proof: always capture the accepted rates into acceptedRates.
              // remoteRate / flexBlock / rateSheet all derive from that, so the
              // accepted overrides survive even if handleZipChange completes
              // afterward and replaces marketAnalysis with the original.
              if (rates) setAcceptedRates(rates);
              // When MarketRateCard generated a fresh analysis (vs. loading a
              // saved rate sheet), keep the city/state/full analysis in sync too.
              if (analysis) {
                setMarketAnalysis({ ...analysis, rates: { ...analysis.rates, ...rates } });
                if (analysis.city)  setMarketCity(analysis.city);
                if (analysis.state) setMarketState(analysis.state);
              } else if (rates) {
                setMarketAnalysis(prev => prev ? { ...prev, rates: { ...prev.rates, ...rates } } : prev);
              }
            }}
          />

          {/* Rate Card Preview */}
          <div style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:6, padding:12, marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#0f1e3c', marginBottom:10 }}>Rate Card Preview</div>
            {!marketAnalysis ? (
              <div style={{ fontSize:11, padding:'12px 0', textAlign:'center', color: marketError ? '#dc2626' : '#9ca3af' }}>
                {marketLoading
                  ? '⏳ Analyzing market rates for ' + (clientZip || 'this ZIP') + '...'
                  : marketError
                    ? '✗ ' + marketError
                    : clientZip && clientZip.length === 5
                      ? 'No market analysis available yet for ZIP ' + clientZip + '.'
                      : 'Enter a ZIP code to see market-adjusted rates'}
              </div>
            ) : (
              rateSheet?.sections.map(section => (
                <div key={section.id} style={{ marginBottom:10 }}>
                  <div style={{ fontSize:9, fontWeight:700, color:'#f97316', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:4 }}>
                    {section.title}
                  </div>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:10 }}>
                    <tbody>
                      {section.items.map((item, i) => (
                        <tr key={i} style={{ borderBottom:'1px solid #f9fafb' }}>
                          <td style={{ padding:'3px 0', color:'#374151' }}>{item.service}</td>
                          <td style={{ padding:'3px 0', textAlign:'right', fontFamily:'DM Mono, monospace', color:'#0f1e3c', fontWeight:500 }}>
                            {fmtRate(item)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))
            )}
          </div>

          {/* Assumptions preview */}
          <div style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:6, padding:12, marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:700, color:'#0f1e3c', marginBottom:8 }}>Assumptions</div>
            {FLEXIT_ASSUMPTIONS.map((a, i) => (
              <div key={i} style={{ display:'flex', gap:6, marginBottom:4 }}>
                <span style={{ color:'#f97316', fontSize:10, flexShrink:0, marginTop:1 }}>•</span>
                <span style={{ fontSize:10, color:'#374151', lineHeight:1.6 }}>{a}</span>
              </div>
            ))}
          </div>

          {/* Documents — FlexIT: Rate Sheet only (assumptions are fixed, payment schedule is inline) */}
          <FlexITDocumentsPanel
            analysis={marketAnalysis}
            settings={settings}
            clientName={recipientBiz}
            recipientContact={recipientContact}
            quoteId={existingQuote?.id}
            quoteNumber={existingQuote?.quote_number}
            quoteStatus={quoteStatus}
            sptProposalId={sptProposalId}
            onSPTLinked={(pid) => setSptProposalId(pid)}
            prepayAmount={prepayAmount}
            remoteRate={remoteRate}
            flexHours={flexHours}
            flexBlockPrice={flexBlock?.blockPrice || 0}
            hasFlexBlock={hasFlexBlock}
            clientEmail={recipientEmail}
            hubspotDealId={hubDealId}
            userId={profile?.id}
            onOpenAddendums={() => setAddendumsModalOpen(true)}
            addendumsCount={addendums.length}
            onOpenServiceOrderPreview={() => setSoPreviewModalOpen(true)}
            effectiveDate={effectiveDate}
            setEffectiveDate={setEffectiveDate}
          />

          {/* v3.5.99.6: client engagement panel — auto-hides on quotes with no proposal/SO activity yet */}
          <EngagementPanel quoteId={existingQuote?.id} />

          {/* Quote Notes */}
          <QuoteNotes
            quoteId={existingQuote?.id}
            quoteNumber={existingQuote?.quote_number}
            clientName={recipientBiz}
            hubDealId={hubDealId}
          />
          <QuoteHistory quoteId={existingQuote?.id} />

        </div>
      </div>
      {/* v3.5.99.4.2: AddendumsModal mount — correct location inside FlexITQuotePage
          where the state variables (addendumsModalOpen, addendums, addendumTemplates,
          setAddendums) are actually in scope. Previously v3.5.99.4 mistakenly placed
          this inside the Sec() helper component which had no access to the state. */}
      <AddendumsModal
        open={addendumsModalOpen}
        onClose={() => setAddendumsModalOpen(false)}
        addendums={addendums}
        templates={addendumTemplates}
        onSave={setAddendums}
      />
      {/* v3.5.99.5: Service Order preview modal */}
      <ServiceOrderPreviewModal
        open={soPreviewModalOpen}
        onClose={() => setSoPreviewModalOpen(false)}
        quoteId={existingQuote?.id}
        docType="flexit_quote"
        clientBiz={recipientBiz}
      />
    </div>
  );
}


// ── FlexIT Documents Panel — Rate Sheet only ──────────────────────────────────
// FlexIT has fixed standard assumptions (shown inline) and no monthly payment schedule.
// Only the Rate Sheet needs to be managed here for SPT export.
function FlexITDocumentsPanel({ analysis, settings, clientName, recipientContact, quoteId, quoteNumber, quoteStatus, sptProposalId, onSPTLinked, prepayAmount, remoteRate, flexHours, flexBlockPrice, hasFlexBlock, clientEmail, hubspotDealId, userId, onOpenAddendums, addendumsCount = 0, onOpenServiceOrderPreview, effectiveDate, setEffectiveDate }) {
  const [showRateSheet, setShowRateSheet] = React.useState(false);
  const [showPayment,   setShowPayment]   = React.useState(false);
  // Stripe Payment #1 amount: flex block price when flex mode, else prepayment
  const stripeAmount = hasFlexBlock ? (flexBlockPrice || 0) : (prepayAmount || 0);
  return (
    <>
      <div style={{ background:'white', border:'1px solid #e5e7eb', borderRadius:6, padding:'10px 12px', marginBottom:10 }}>
        <div style={{ fontSize:9, fontWeight:700, textTransform:'uppercase', letterSpacing:'.07em', color:'#6b7280', marginBottom:8 }}>
          📄 Documents
        </div>
        {/* v3.5.99.5: Preview Service Order — opens the full SO PDF inline.
            Opt-in via callback (same pattern as onOpenAddendums). */}
        {onOpenServiceOrderPreview && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 10px', background:'#f8fafc', borderRadius:4, border:'1px solid #e5e7eb', marginBottom:5 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                <span style={{ fontSize:11 }}>👁</span>
                <span style={{ fontSize:11, fontWeight:600, color:'#0f1e3c' }}>Preview Service Order</span>
              </div>
              <div style={{ fontSize:9, color:'#9ca3af', marginTop:1, paddingLeft:16 }}>
                See the full document the client will sign — rendered PDF includes all sections, rates, and addendums
              </div>
            </div>
            <button onClick={onOpenServiceOrderPreview}
              style={{ padding:'4px 10px', background:'#0f1e3c', color:'white', border:'none', borderRadius:4, fontSize:10, fontWeight:600, cursor:'pointer', flexShrink:0 }}>
              Preview
            </button>
          </div>
        )}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 10px', background:'#f8fafc', borderRadius:4, border:`1px solid ${sptProposalId ? '#bbf7d0' : '#e5e7eb'}` }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:5 }}>
              <span style={{ fontSize:11 }}>💲</span>
              <span style={{ fontSize:11, fontWeight:600, color:'#0f1e3c' }}>Rate Card / Rate Schedule</span>
              {sptProposalId && <span style={{ fontSize:8, fontWeight:700, color:'#166534', background:'#dcfce7', padding:'1px 5px', borderRadius:3 }}>SPT ✓</span>}
            </div>
            <div style={{ fontSize:9, color:'#9ca3af', marginTop:1, paddingLeft:16 }}>
              {sptProposalId ? 'Linked to Smart Pricing Table' : 'Market-adjusted T&M rates · link or create in SPT'}
            </div>
          </div>
          <button onClick={() => setShowRateSheet(true)}
            style={{ padding:'4px 10px', background:'#0f1e3c', color:'white', border:'none', borderRadius:4, fontSize:10, fontWeight:600, cursor:'pointer', flexShrink:0 }}>
            {sptProposalId ? 'View / Update' : 'View / Export'}
          </button>
        </div>
        {/* v3.5.99.4.1: Special Terms (Addendums) — opt-in row; renders only when
            onOpenAddendums callback is provided. Same row pattern as Rate Card above. */}
        {onOpenAddendums && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 10px', background:'#f8fafc', borderRadius:4, border:'1px solid #e5e7eb', marginTop:5 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                <span style={{ fontSize:11 }}>📎</span>
                <span style={{ fontSize:11, fontWeight:600, color:'#0f1e3c' }}>Special Terms</span>
                {addendumsCount > 0 && (
                  <span style={{ fontSize:8, fontWeight:700, color:'#1e40af', background:'#dbeafe', padding:'1px 5px', borderRadius:3 }}>
                    {addendumsCount} attached
                  </span>
                )}
              </div>
              <div style={{ fontSize:9, color:'#9ca3af', marginTop:1, paddingLeft:16 }}>
                {addendumsCount > 0
                  ? `${addendumsCount} addendum${addendumsCount === 1 ? '' : 's'} attached \u00b7 renders in the Service Order PDF`
                  : 'Optional addendum templates (Supersedes, M2M renewal, ETF waiver, etc.)'}
              </div>
            </div>
            <button onClick={onOpenAddendums}
              style={{ padding:'4px 10px', background:'#0f1e3c', color:'white', border:'none', borderRadius:4, fontSize:10, fontWeight:600, cursor:'pointer', flexShrink:0 }}>
              {addendumsCount > 0 ? 'Edit' : 'Add'}
            </button>
          </div>
        )}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 10px', background:'#f8fafc', borderRadius:4, border:'1px solid #e5e7eb', marginTop:5 }}>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:5 }}>
              <span style={{ fontSize:11 }}>💳</span>
              <span style={{ fontSize:11, fontWeight:600, color:'#0f1e3c' }}>Payment Schedule & Terms</span>
            </div>
            <div style={{ fontSize:9, color:'#9ca3af', marginTop:1, paddingLeft:16 }}>Month to Month · Due Upon Receipt · prepayment at signing</div>
          </div>
          <button onClick={() => setShowPayment(true)}
            style={{ padding:'4px 10px', background:'#0f1e3c', color:'white', border:'none', borderRadius:4, fontSize:10, fontWeight:600, cursor:'pointer', flexShrink:0 }}>
            View
          </button>
        </div>
      </div>

      {/* v3.5.99.7.24: optional back-dated agreement effective date */}
      <AgreementEffectiveDate value={effectiveDate} onChange={setEffectiveDate} />

      {/* SignWell signature card — sends FlexIT agreement for client signature */}
      {stripeAmount > 0 && (
        <SignatureCard docType="flexit_quote"
          quoteId={quoteId}
          quoteStatus={quoteStatus}
          quoteNumber={quoteNumber}
          clientName={clientName}
          clientContact={recipientContact}
          clientEmail={clientEmail}
          upfrontAmount={stripeAmount}
          userId={userId}
        />
      )}

      {/* Stripe prepayment card — flex block price when flex mode, else prepayment amount */}
      {stripeAmount > 0 && (
        <StripePaymentCard
          quoteId={quoteId}
          quoteType="flex"
          quoteNumber={quoteNumber}
          clientName={clientName}
          clientEmail={clientEmail}
          recipientContact={recipientContact}
          prepayAmount={stripeAmount}
          hubspotDealId={hubspotDealId}
        />
      )}

      {showPayment && React.createElement(
        FlexITPaymentModal,
        { onClose: () => setShowPayment(false), prepayAmount, remoteRate, clientName, settings, flexHours, flexBlockPrice, hasFlexBlock }
      )}

      {showRateSheet && React.createElement(
        RateSheetModalComp,
        {
          onClose: () => setShowRateSheet(false),
          analysis, settings, clientName, recipientContact,
          quoteId, quoteNumber, sptProposalId,
          onSPTLinked: (pid, url) => {
            onSPTLinked?.(pid, url);
            if (!pid && quoteId) supabase.from('quotes').update({ spt_proposal_id: null }).eq('id', quoteId);
          },
        }
      )}
    </>
  );
}


// ── FlexIT Payment Schedule Modal ─────────────────────────────────────────────
function FlexITPaymentModal({ onClose, prepayAmount, remoteRate, clientName, settings, flexHours, flexBlockPrice, hasFlexBlock }) {
  const fmt2 = n => n != null ? `$${Number(n).toFixed(2)}` : '—';
  const checkFee   = parseFloat(settings?.payment_check_fee    || 10);
  const ccSurcharge= parseFloat(settings?.payment_cc_surcharge || 0.02);

  // Match the on-screen Billing Summary logic: when a flex block is selected
  // it IS the upfront — the modal shows a single "Flex Block" row instead of
  // a separate Initial Prepayment row. The Setup Services Fee paragraph below
  // also reflects whichever fee actually applies.
  const upfrontLabel  = hasFlexBlock ? `#1 — Flex Block — ${flexHours}hrs Pre-Purchased` : '#1 — Initial Prepayment';
  const upfrontAmount = hasFlexBlock ? flexBlockPrice : prepayAmount;
  const upfrontDue    = hasFlexBlock
    ? 'Upon Agreement Signing — Block valid 12 months · refillable at this rate'
    : 'Upon Agreement Signing';

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'stretch', justifyContent:'flex-end', zIndex:600 }}>
      <div style={{ flex:1 }} onClick={onClose} />
      <div style={{ width:620, background:'white', display:'flex', flexDirection:'column', boxShadow:'-8px 0 40px rgba(0,0,0,0.2)' }}>

        {/* Header */}
        <div style={{ background:'#0f1e3c', padding:'14px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:'white' }}>Payment Schedule & Terms</div>
            <div style={{ fontSize:10, color:'#64748b', marginTop:1 }}>{clientName || 'Client'} · FlexIT On-Demand · Month to Month</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'#64748b', fontSize:22, cursor:'pointer', lineHeight:1 }}>×</button>
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:20 }}>

          {/* Term header */}
          <div style={{ background:'#fff7ed', border:'1px solid #fed7aa', borderRadius:6, padding:'10px 14px', marginBottom:16, textAlign:'center' }}>
            <span style={{ fontSize:13, fontWeight:700, color:'#c2410c' }}>Term of this agreement is MONTH TO MONTH</span>
          </div>

          {/* Payment table */}
          <div style={{ marginBottom:16 }}>
            <table style={{ width:'100%', borderCollapse:'collapse', border:'1px solid #e5e7eb', borderRadius:6, overflow:'hidden' }}>
              <thead>
                <tr style={{ background:'#0f1e3c' }}>
                  {['Payment', 'Amount', 'Due Date'].map(h => (
                    <th key={h} style={{ padding:'9px 12px', fontSize:10, fontWeight:700, color:'white', textAlign:'left', textTransform:'uppercase', letterSpacing:'.04em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: upfrontAmount > 0 ? '#fffbeb' : 'white', borderBottom:'1px solid #e5e7eb' }}>
                  <td style={{ padding:'10px 12px', fontSize:11, fontWeight:700, color: hasFlexBlock ? '#c2410c' : '#374151' }}>{upfrontLabel}</td>
                  <td style={{ padding:'10px 12px', fontSize:12, fontFamily:'DM Mono, monospace', fontWeight:700, color:'#c2410c' }}>
                    {upfrontAmount > 0 ? fmt2(upfrontAmount) : '$0.00'}
                  </td>
                  <td style={{ padding:'10px 12px', fontSize:11, color:'#6b7280' }}>{upfrontDue}</td>
                </tr>
              </tbody>
            </table>
            <div style={{ fontSize:9, color:'#dc2626', fontWeight:600, marginTop:5 }}>* Payment #1 is non-refundable.</div>
          </div>

          {/* Out of scope */}
          <div style={{ background:'#fef3c7', border:'1px solid #fde68a', borderRadius:5, padding:'8px 12px', marginBottom:16, fontSize:11, color:'#92400e', lineHeight:1.6 }}>
            Any items not explicitly included in this quote are considered out of scope. Out-of-scope services will be quoted separately as a project or billed at the then-current hourly rates, subject to customer approval prior to commencement of work.
          </div>

          {/* Billing & Payment Terms sections — match the PDF exactly */}
          <SectionHeader title="Billing & Payment Terms" />

          <SubSec title="Automatic Payments (Required)">
            For recurring billing purposes, the Client is required to maintain a valid <strong>ACH/EFT or credit card</strong> on file for automatic payment processing. In lieu of automatic debit, the Client may elect to remit payment via <strong>direct EFT, bank wire, or ACH transfer</strong> using our online billing portal at{' '}
            <a href="https://ferrumit.com/billing" target="_blank" rel="noopener noreferrer" style={{ color:'#2563eb' }}>ferrumit.com/billing</a>.
            {' '}Account and remittance information may be obtained by contacting Ferrum Technology Services, LLC's finance team at{' '}
            <a href="mailto:billing@ferrumit.com" style={{ color:'#2563eb' }}>billing@ferrumit.com</a>.
            <div style={{ background:'#fef9c3', border:'1px solid #fde047', borderRadius:4, padding:'6px 8px', marginTop:6, fontSize:10, color:'#713f12', lineHeight:1.5 }}>
              Payment information must be submitted or confirmed prior to the commencement of services. Recurring invoices will be processed automatically using the agreed payment method in accordance with the billing schedule.
            </div>
          </SubSec>

          <SubSec title="Invoicing Terms">
            Services are invoiced with <strong>Due Upon Receipt</strong> payment terms. Invoices will be automatically paid on the due date using the payment method on file.
          </SubSec>

          <SubSec title="Paper Check Payments">
            While electronic payment is required for recurring billing, any paper checks received will incur a <strong>${checkFee.toFixed(0)} administrative processing and handling fee</strong>.
          </SubSec>

          {ccSurcharge > 0 && (
            <SubSec title="Credit Card Payments">
              A <strong>{(ccSurcharge * 100).toFixed(0)}% surcharge</strong> applies to all credit card transactions. ACH/EFT is the recommended payment method.
            </SubSec>
          )}

          <SubSec title="Purchase Orders">
            If a purchase order is required, please submit it to{' '}
            <a href="mailto:billing@ferrumit.com" style={{ color:'#2563eb' }}>billing@ferrumit.com</a> prior to invoicing.
          </SubSec>

          <SectionHeader title={hasFlexBlock ? 'Flex Block Pre-Purchase' : 'Setup Services Fee'} />
          <div style={{ fontSize:11, color:'#374151', lineHeight:1.7, marginBottom:14 }}>
            {hasFlexBlock
              ? <>
                  This engagement is initiated with the <strong>{flexHours}-hour Flex Block pre-purchase</strong> ({fmt2(flexBlockPrice)}) listed above. The block fee is paid in full upon agreement signing and serves as the engagement's initial commitment — no separate hourly pre-payment is required. Time on engagements is drawn from the block first; once the block is exhausted, additional time is billed at the published rates per the Rate Card. The block is valid for 12 months from the purchase date and may be refilled at the original agreed-upon rate within 30 days of depletion or expiration.
                </>
              : <>
                  A one-time setup, configuration, and installation fee{prepayAmount > 0 ? ` of ${fmt2(prepayAmount)}` : ''} is due at the start of the agreement and will be billed as specified in this Quote.
                </>}
          </div>

          <SectionHeader title="Declined or Returned Payments" />
          <div style={{ fontSize:11, color:'#374151', lineHeight:1.7, marginBottom:14 }}>
            Any declined or returned payments may incur a <strong>$50 administrative fee</strong>.
          </div>

          <SectionHeader title="Hardware, Software, Licensing & Third-Party Services" />
          <div style={{ fontSize:11, color:'#374151', lineHeight:1.7, marginBottom:14 }}>
            All sales of hardware, software, licensing, and third-party services are final. Please review and verify all orders prior to submission, as returns or refunds are not available.
          </div>

        </div>
      </div>
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div style={{ background:'#0f1e3c', borderRadius:5, padding:'7px 14px', textAlign:'center', marginBottom:10 }}>
      <span style={{ fontSize:13, fontWeight:700, color:'white' }}>{title}</span>
    </div>
  );
}

function SubSec({ title, children }) {
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ fontSize:11, fontWeight:700, color:'#374151', marginBottom:3 }}>{title}</div>
      <div style={{ fontSize:11, color:'#6b7280', lineHeight:1.7 }}>{children}</div>
    </div>
  );
}
